import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import StoreGrid from './StoreGrid';
import Header from './header';
import Footer from './footer';

// Use relative API path (Nginx will forward /api/... to Express on port 5000)
const API_BASE = '/api';

const StorePage = () => {
  const { city, storeName } = useParams();
  const [storeInfo, setStoreInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Character limit for truncated description
  const descriptionLimit = 150;

  useEffect(() => {
    // Fetch store information from your relative /api endpoint
    fetch(`${API_BASE}/stores/${storeName}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load store information');
        }
        return response.json();
      })
      .then(data => {
        setStoreInfo(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching store details:', err);
        setError('Unable to load store information');
        setLoading(false);
      });
  }, [storeName]);

  // Function to truncate description
  const getTruncatedDescription = (text) => {
    if (!text) return '';
    if (text.length <= descriptionLimit) return text;
    return showFullDescription ? text : `${text.substring(0, descriptionLimit)}...`;
  };

  // Handle different image URL patterns
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    // Handle server/uploads URLs directly
    if (imageUrl.startsWith('/server/uploads')) {
      return imageUrl;
    }
    
    // For other paths, use the API_BASE prefix
    return `${API_BASE}${imageUrl}`;
  };

  return (
    <div>
      <Header />

      <header className="bg-light shadow-sm mb-4">
        <div className="container py-4">
          <div className="row align-items-center">
            {/* Logo Column - Larger Size */}
            <div className="col-md-3 text-center mb-3 mb-md-0">
              {loading ? (
                <div className="placeholder-glow">
                  <div
                    className="placeholder rounded"
                    style={{ width: '200px', height: '200px', maxWidth: '100%' }}
                  />
                </div>
              ) : storeInfo?.profile_image_url ? (
                <>
                  {!imageError ? (
                    <img
                      src={getImageUrl(storeInfo.profile_image_url)}
                      alt={`${storeInfo.name} logo`}
                      className="rounded border"
                      style={{
                        width: '200px',
                        height: '200px',
                        objectFit: 'cover',
                        backgroundColor: '#f8f9fa',
                        maxWidth: '100%',
                      }}
                      onError={(e) => {
                        // If the image fails to load, set state to show fallback
                        setImageError(true);
                      }}
                    />
                  ) : (
                    <div
                      className="rounded border d-flex align-items-center justify-content-center bg-white mx-auto"
                      style={{ 
                        width: '200px', 
                        height: '200px',
                        maxWidth: '100%',
                      }}
                    >
                      <span className="display-1 text-muted">
                        {storeInfo?.name?.charAt(0)?.toUpperCase() || ''}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="rounded border d-flex align-items-center justify-content-center bg-white mx-auto"
                  style={{ width: '200px', height: '200px', maxWidth: '100%' }}
                >
                  <span className="display-1 text-muted">
                    {storeInfo?.name?.charAt(0)?.toUpperCase() || ''}
                  </span>
                </div>
              )}
            </div>

            {/* Store Details Column */}
            <div className="col-md-9">
              {loading ? (
                <div className="placeholder-glow">
                  <div className="placeholder col-6 mb-2" />
                  <div className="placeholder col-4" />
                </div>
              ) : (
                <>
                  <h1 className="display-5 mb-0">{storeInfo?.name || storeName}</h1>
                  <p className="text-muted fs-5">{storeInfo?.city || city}</p>
                  {storeInfo?.description && (
                    <div className="mt-3">
                      <p className="lead">
                        {getTruncatedDescription(storeInfo.description)}
                        {storeInfo.description.length > descriptionLimit && (
                          <button
                            className="btn btn-link p-0 ms-2"
                            onClick={() => setShowFullDescription(!showFullDescription)}
                          >
                            {showFullDescription ? 'Show Less' : 'Show More'}
                          </button>
                        )}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Contact Information */}
              {storeInfo && !loading && (
                <div className="mt-3">
                  <div className="d-flex flex-wrap align-items-center gap-4">
                    {/* Website */}
                    {storeInfo.website && (
                      <div className="d-flex align-items-center">
                        <i className="bi bi-globe me-2 text-primary"></i>
                        <a
                          href={
                            storeInfo.website.startsWith('http')
                              ? storeInfo.website
                              : `https://${storeInfo.website}`
                          }
                          className="text-decoration-none"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {storeInfo.website
                            .replace(/(^\w+:|^)\/\//, '')
                            .replace(/\/$/, '')}
                        </a>
                      </div>
                    )}

                    {/* Phone number */}
                    {storeInfo.phone_number && (
                      <div className="d-flex align-items-center">
                        <i className="bi bi-telephone me-2 text-primary"></i>
                        <a
                          href={`tel:${storeInfo.phone_number}`}
                          className="text-decoration-none"
                        >
                          {storeInfo.phone_number.replace(
                            /(\d{3})(\d{3})(\d{4})/,
                            '$1 - $2 - $3'
                          )}
                        </a>
                      </div>
                    )}

                    {/* Email link */}
                    {storeInfo.contact_email && (
                      <div className="d-flex align-items-center">
                        <i className="bi bi-envelope me-2 text-primary"></i>
                        <a
                          href={`mailto:${storeInfo.contact_email}`}
                          className="text-decoration-none"
                        >
                          {storeInfo.contact_email}
                        </a>
                      </div>
                    )}

                    {/* Instagram link */}
                    {storeInfo.social_media?.instagram && (
                      <a
                        href={
                          storeInfo.social_media.instagram.startsWith('http')
                            ? storeInfo.social_media.instagram
                            : `https://instagram.com/${storeInfo.social_media.instagram.replace('@', '')}`
                        }
                        className="text-decoration-none"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {/* Instagram icon */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          className="text-primary"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.927 3.927 0 0 0 .923-.923 3.917 3.917 0 0 0 .372-1.942c.038-.853.048-1.125.048-3.298s-.01-2.444-.048-3.298c-.04-.851-.174-1.433-.372-1.942a3.927 3.927 0 0 0-.923-.923A3.917 3.917 0 0 0 11.298.42C10.79.222 10.208.087 9.297.048 8.445.01 8.173 0 7.999 0h.001zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919 2.48 2.48 0 0 1-.92.598c-.281.11-.705.24-1.485.276-.843.038-1.096.047-3.232.047s-2.389-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.598-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.232s.008-2.388.046-3.231c.036-.78.166-1.203.275-1.485a2.474 2.474 0 0 1 .598-.92 2.478 2.478 0 0 1 .92-.599c.281-.11.705-.24 1.485-.275.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              {error}
            </div>
          )}
        </div>
      </header>

      {/* Product Grid */}
      <StoreGrid storeName={storeName} />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default StorePage;